import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import vars from '../variables';
import { validateCanonicalLinkWithLocale } from '../utils/utils';

type MetaDataProps = {
  title: string;
  metaTitle: string;
  metaDescription: string;
  metaImage: string;
  socialCardTitle: string;
  socialCardDescription: string;
  canonicalLink: string;
  localeCode: string;
};

type GtmVarsProps = {
  pageId: string;
  event: string;
  pageType: string | null;
  lastUpdated: string;
  topics: string[];
  geography: string;
};

type HrefLangUrlsProps = {
  url: string;
  localeCode: string;
};

interface HeadProps {
  metaData: MetaDataProps;
  gtmVars: GtmVarsProps[];
  hreflangUrls: HrefLangUrlsProps[];
  pageUrl: string | null;
}

const Head: React.FC<HeadProps> = ({
  metaData,
  gtmVars,
  hreflangUrls,
  pageUrl = null,
}) => {
  const ogImage = metaData.metaImage ? metaData.metaImage : vars.defaultOgImage;
  const twImage = metaData.metaImage ? metaData.metaImage : vars.defaultTwImage;
  const { canonicalLink, localeCode } = metaData;

  const shouldDisplayCanonicalLink = validateCanonicalLinkWithLocale(
    canonicalLink,
    localeCode
  );

  useEffect(() => {
    if (pageUrl === '/newsletter-sign-up') {
      // Function to load the Mailchimp script dynamically
      const loadMailchimpScript = (): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
          if (
            document.querySelector(
              'script[src="//s3.amazonaws.com/downloads.mailchimp.com/js/mc-validate.js"]'
            )
          ) {
            resolve();
          } else {
            const script = document.createElement('script');
            script.src =
              '//s3.amazonaws.com/downloads.mailchimp.com/js/mc-validate.js';
            script.async = true;
            script.onload = () => resolve();
            script.onerror = () =>
              reject(
                new Error('Failed to load the Mailchimp validation script.')
              );
            document.body.appendChild(script);
          }
        });
      };

      loadMailchimpScript()
        .then(() => {
          if (window) {
            (function ($) {
              window.fnames = [];
              window.ftypes = [];
              window.fnames[0] = 'EMAIL';
              window.ftypes[0] = 'email';
              window.fnames[1] = 'FNAME';
              window.ftypes[1] = 'text';
              window.fnames[2] = 'LNAME';
              window.ftypes[2] = 'text';
              window.fnames[5] = 'ORG';
              window.ftypes[5] = 'text';
              window.fnames[3] = 'JOB';
              window.ftypes[3] = 'text';
              window.fnames[9] = 'ROLE';
              window.ftypes[9] = 'dropdown';
              window.fnames[10] = 'LEVEL';
              window.ftypes[10] = 'dropdown';
              window.fnames[12] = 'CE3690983B';
              window.ftypes[12] = 'text';
              window.fnames[16] = 'F1CD816FA5';
              window.ftypes[16] = 'text';
              window.fnames[15] = '64CF6EB658';
              window.ftypes[15] = 'text';
              window.fnames[14] = '6499A52E8D';
              window.ftypes[14] = 'text';
              window.fnames[13] = 'D1D21C44DB';
              window.ftypes[13] = 'text';
              window.fnames[11] = '845AE651FC';
              window.ftypes[11] = 'text';
              window.fnames[8] = 'NETWORKMEM';
              window.ftypes[8] = 'text';
              window.fnames[4] = 'MMERGE4';
              window.ftypes[4] = 'text';
              window.fnames[6] = 'DEPARTMENT';
              window.ftypes[6] = 'text';
              window.fnames[7] = 'UNIVERSITY';
              window.ftypes[7] = 'text';
            })(window.jQuery);

            window.$mcj = window.jQuery.noConflict(true);
          } else {
            console.error(
              'Mailchimp script loaded, but jQuery is not available.'
            );
          }
        })
        .catch(() => {
          console.error('Failed to load the Mailchimp validation script.');
        });
    }
  }, [pageUrl]);

  return (
    <Helmet htmlAttributes={{ lang: localeCode }}>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" cross-origin />
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@200;400;500;600;700;800&display=swap"
        rel="stylesheet"
      ></link>
      {pageUrl &&
        pageUrl === '/topics/circular-economy-introduction/overview' && (
          <script type="application/ld+json">
            {`{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": {
            "@type": "Question",
            "name": "What is Circular Economy?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "The circular economy tackles climate change and other global challenges like biodiversity loss, waste, and pollution, by decoupling economic activity from the consumption of finite resources."
            }
          }
        }`}
          </script>
        )}
      {/* Google Consent Mode v2 Initialization*/}
      {process.env.GATSBY_ENV === 'production' && (
        <script data-cookieconsent="ignore">
          {`window.dataLayer = window.dataLayer || [];
        function gtag() { dataLayer.push(arguments); }
        if(localStorage.getItem('consentMode') === null) {
            gtag('consent', 'default', {
                'ad_personalization': 'denied',
                'ad_storage': 'denied',
                'ad_user_data': 'denied',
                'analytics_storage': 'denied',
                'personalization_storage': 'denied',
                'functionality_storage': 'denied',
                'security_storage': 'denied',
            });
            gtag('set', 'ads_data_redaction', true);
            gtag('set', 'url_passthrough', true);
        }`}
        </script>
      )}
      {process.env.GATSBY_ENV === 'production' && (
        <script id="google-tag-manager-script" data-cookieconsent="ignore">
          {`(function(w,d,s,l,i){
            w[l] = w[l] || [];
            w[l].push({'gtm.start': new Date().getTime(), event: 'gtm.js'});
            var f = d.getElementsByTagName(s)[0],
                j = d.createElement(s), dl = l != 'dataLayer' ? '&l='+l : '';
            j.async = true; j.src = 'https://www.googletagmanager.com/gtm.js?id='+i+dl;
            f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${process.env.GATSBY_GOOGLE_TAG_MANAGER_ID}');`}
        </script>
      )}
      {process.env.GATSBY_ENV === 'production' && (
        <script id="initiate-datalayer-script">
          {`(function() {
            function gatherText() {
              return document.body.innerText ? document.body.innerText.split('\\n').filter(innerPiece => innerPiece !== '').join(' ') : '';
            }
            function countWords(text) {
              return text.split(/\\s+/).filter(word => word.length > 0).length;
            }
            function getInterval(count) {
              const intervalStart = Math.floor(count / 500) * 500;
              return \`\${intervalStart}-\${intervalStart + 500}\`;
            }
        
            window.dataLayer = window.dataLayer || [];
            const innerText = document.body.innerText;
            if (innerText) {
              const text = gatherText();
              window.dataLayer.push({
                ...${JSON.stringify(gtmVars)},
                'wordCount': getInterval(countWords(text)),
              });
            } else {
              window.dataLayer.push({
                ...${JSON.stringify(gtmVars)},
                'wordCount': '0',
              });
            }
          })();`}
        </script>
      )}
      {process.env.GATSBY_ENV === 'production' && (
        /* Hotjar */
        <script id="hotjar script">
          {`(function(h,o,t,j,a,r){
              h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
              h._hjSettings={hjid:${process.env.GATSBY_HOTJAR_ID},hjsv:6};
              a=o.getElementsByTagName('head')[0];
              r=o.createElement('script');r.async=1;
              r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
              a.appendChild(r);
            })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`}
        </script>
      )}
      <script src="https://www.googleoptimize.com/optimize.js?id=OPT-PLRGRK7"></script>
      <script
        type="text/javascript"
        src="https://acrobatservices.adobe.com/view-sdk/viewer.js"
      ></script>

      <script>
        {`(function(e,t,o,n,p,r,i){e.visitorGlobalObjectAlias=n;e[e.visitorGlobalObjectAlias]=e[e.visitorGlobalObjectAlias]||function(){(e[e.visitorGlobalObjectAlias].q=e[e.visitorGlobalObjectAlias].q||[]).push(arguments)};e[e.visitorGlobalObjectAlias].l=(new Date).getTime();r=t.createElement("script");r.src=o;r.async=true;i=t.getElementsByTagName("script")[0];i.parentNode.insertBefore(r,i)})(window,document,"https://diffuser-cdn.app-us1.com/diffuser/diffuser.js","vgo");
          vgo('setAccount', '612925126');
          vgo('setTrackByDefault', true);
          vgo('process');`}
      </script>

      <title>{metaData && metaData.metaTitle}</title>
      <meta name="description" content={metaData && metaData.metaDescription} />
      {hreflangUrls &&
        hreflangUrls.map(link => {
          return (
            <link
              key={link.localeCode}
              rel="alternate"
              hrefLang={link.localeCode.slice(0, 2)}
              href={`https://www.ellenmacarthurfoundation.org${link.url}`}
            />
          );
        })}

      <meta
        name="facebook-domain-verification"
        content="izukwa67afzku3qykvvabwk1l0pvlj"
      />
      <meta
        property="og:title"
        content={metaData && metaData.socialCardTitle}
      />
      <meta property="og:type" content="article" />
      {metaData && metaData.socialCardDescription && (
        <meta
          property="og:description"
          content={metaData && metaData.socialCardDescription}
        />
      )}
      <meta property="og:image" content={ogImage} />
      <meta name="twitter:card" content="summary" />
      <meta
        name="twitter:title"
        content={metaData && metaData.socialCardTitle}
      />
      {metaData && metaData.socialCardDescription && (
        <meta
          name="twitter:description"
          content={metaData && metaData.socialCardDescription}
        />
      )}
      <meta name="twitter:image:src" content={twImage} />
      {shouldDisplayCanonicalLink && (
        <link rel="canonical" href={canonicalLink} />
      )}
    </Helmet>
  );
};

export default Head;
